<script setup>
import { computed, watch } from 'vue'

import InputRadio from '@/components/form/InputRadio.vue'
import FunnelStep from '@/components/FunnelStep.vue'
import { useAddressStore } from '@/stores/AddressStore'
import { useFormDataStore } from '@/stores/FormDataStore'
import { useHelpers } from '@/useHelpers'

const addressStore = useAddressStore()
const dataStore = useFormDataStore()
const { sendMessage } = useHelpers()

defineProps({
  id: {
    type: Number,
    required: true
  },
  address: {
    type: Object,
    required: true
  },
  isActive: {
    type: Boolean,
    required: true
  },
  isCompleted: {
    type: Boolean,
    required: true
  },
})

function openPacketaWidget() {
  sendMessage('selectPickupPoint', {})
}

const pickupPointAddress = computed(() => {
  if (addressStore.packetaPickupPoint) {
    const { street, zip, city } = addressStore.packetaPickupPoint
    return `${street}, ${zip}, ${city}`
  }
  return ''
})

const pickupPointPlace = computed(() => {
  return addressStore.packetaPickupPoint?.place ?? ''
})

watch(
  () => addressStore.packetaPickupPoint,
  (point) => {
    // TODO: verify pickup point
    dataStore.setPacketaPickupPoint(point)
  }
)

const emits = defineEmits(['onClickCompleteStep', 'onChangeClicked'])

</script>

<template>
  <FunnelStep
    :id="id"
    :details="$t('Delivery method')"
    :is-active="isActive"
    :is-completed="isCompleted"
    :name="$t(addressStore.packetaDelivery ? 'Packeta' : 'Post')"
    :step-label-next-line="addressStore.packetaDelivery"
    @on-change-clicked="emits('onChangeClicked')"
  >
    <template v-slot:completedHeaderContent>
      <div v-if="addressStore.packetaDelivery && addressStore.packetaPickupPoint" class="pickup-point-info header-offset">
        <ul class="pickup-list">
          <li class="place-name">{{ pickupPointPlace }}</li>
          <li>{{ pickupPointAddress }}</li>
          <li v-html="addressStore.packetaPickupPoint.openingHours.compactShort"></li>
        </ul>
      </div>
    </template>

    <div class="delivery-radio-group">
      <InputRadio
        v-model="addressStore.selectedDeliveryMethod"
        name="delivery_method"
        :label="$t('Home delivery')"
        :secondary-label="$t('Post')"
        value="posta"
      />
      <InputRadio
          v-model="addressStore.selectedDeliveryMethod"
          :label="$t('Delivery to pickup point')"
          :hide-bottom-border="true"
          :secondary-label="$t('Packeta')"
          name="delivery_method"
          value="zasilkovna"
      >
        <div v-if="addressStore.packetaDelivery" class="delivery-content-wrapper">
          <div v-if="addressStore.packetaPickupPoint" class="pickup-point-info">
            <ul class="pickup-list">
              <li class="place-name">{{ pickupPointPlace }}</li>
              <li>{{ pickupPointAddress }}</li>
              <li v-html="addressStore.packetaPickupPoint.openingHours.compactShort"></li>
            </ul>
            <button class="link font-size-normal" @click="openPacketaWidget">
              {{ $t('Change pickup point') }}
            </button>
          </div>

          <a v-else
             class="link font-size-normal"
             @click="openPacketaWidget"
          >
            {{ $t('Select pickup point') }}
          </a>
        </div>
      </InputRadio>
    </div>

    <div class="input-group button-group">
      <button
        class="button button--large button--primary step-address__button"
        :disabled="addressStore.packetaDelivery && !addressStore.packetaPickupPoint"
        @click="$emit('onClickCompleteStep')"
      >
        {{ $t('Continue to shipping address') }}
      </button>
    </div>
  </FunnelStep>
</template>

<style lang="scss">
  .delivery-radio-group {
    display: flex;
    flex-direction: column;
    gap: 16px;

    .delivery-content-wrapper {
      width: 100%;
      padding-top: 16px;
    }
  }

  .pickup-point-info {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    font-size: 16px;
    gap: 8px;

    &.header-offset {
      padding: 8px 32px;
    }

    > ul.pickup-list {
      color: var(--color-text-secondary);
      font-weight: 500;
      display: flex;
      flex-direction: column;
      gap: 6px;
      margin: 0;

      > li.place-name {
        font-weight: bold;
        color: var(--color-text-primary);
      }
    }
  }
</style>
